import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Microscope, Shield, Users, Award, Globe, TrendingUp, CheckCircle2, Beaker, Stethoscope } from 'lucide-react';
import aplenLogo from '../images/aplen_logo.png';

export default function Home() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // const stats = [
  //   { label: 'Countries Served', value: '50+', icon: Globe },
  //   { label: 'Research Programs', value: '25+', icon: Microscope },
  //   { label: 'WHO-GMP Facilities', value: '100%', icon: Shield },
  //   { label: 'Team Members', value: '500+', icon: Users },
  // ];

  const values = [
    {
      icon: Award,
      title: 'Quality & Compliance',
      description: 'Adhering to the highest global standards in pharmaceutical manufacturing and quality control.',
    },
    {
      icon: Microscope,
      title: 'Scientific Integrity',
      description: 'Evidence-based research and rigorous clinical validation drive all our development programs.',
    },
    {
      icon: Stethoscope,
      title: 'Patient First',
      description: 'Every decision we make prioritizes patient safety, efficacy, and access to essential medicines.',
    },
    {
      icon: CheckCircle2,
      title: 'Ethical Practices',
      description: 'Transparency, accountability, and ethical conduct form the foundation of our operations.',
    },
  ];

  // const therapeuticAreas = [
  //   { name: 'Oncology', color: 'bg-primary', count: '8 products' },
  //   { name: 'Immunology', color: 'bg-secondary', count: '6 products' },
  //   { name: 'Endocrinology', color: 'bg-accent', count: '5 products' },
  //   { name: 'Autoimmune', color: 'bg-primary', count: '7 products' },
  // ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        
        <div className="relative container-custom py-10 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`space-y-8 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
              <div className="space-y-4">
                <div className="flex flex-col items-center space-y-3 lg:flex-row lg:space-y-0 lg:space-x-3">
                  <img src={aplenLogo} alt="Apken Pharma" className="h-16 w-auto rounded-md lg:h-14" />
                  <span className="text-sm font-medium text-muted-foreground tracking-wide text-center lg:text-left">
                    Apken Pharma
                  </span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-foreground font-['Space_Grotesk']">
                  Advancing Healthcare Through{' '}
                  <span className="text-primary">Innovation</span>
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                  Apken Pharma is committed to delivering high-quality, affordable medicines that improve patient outcomes globally through scientific excellence and ethical practices.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/our-business">
                  <Button size="lg" className="w-full sm:w-auto">
                    Our Business
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image Placeholder - Professional Medical Imagery */}
            <div className={`relative ${isVisible ? 'animate-fade-in animation-delay-200' : 'opacity-0'}`}>
              <div className="relative aspect-[3/4] sm:aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10 p-4 sm:p-8">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 backdrop-blur-3xl" />
                <div className="relative h-full flex items-start justify-center p-2 sm:p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full max-w-full">
                    {/* <div className="bg-card rounded-xl p-3 sm:p-6 shadow-lg card-hover border border-border">
                      <div className="flex flex-col items-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 text-center">
                        <Microscope className="h-6 sm:h-8 w-6 sm:w-8 text-primary flex-shrink-0" />
                        <h4 className="font-semibold text-foreground text-sm sm:text-base">Research</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground text-center">Advanced biosimilar development</p>
                    </div> */}
                    <div className="bg-card rounded-xl p-3 sm:p-6 shadow-lg card-hover border border-border">
                      <div className="flex flex-col items-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 text-center">
                        <Shield className="h-6 sm:h-8 w-6 sm:w-8 text-secondary flex-shrink-0" />
                        <h4 className="font-semibold text-foreground text-sm sm:text-base">Quality</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground text-center">WHO-GMP certified facilities</p>
                    </div>
                    {/* <div className="bg-card rounded-xl p-3 sm:p-6 shadow-lg card-hover border border-border">
                      <div className="flex flex-col items-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 text-center">
                        <Beaker className="h-6 sm:h-8 w-6 sm:w-8 text-accent flex-shrink-0" />
                        <h4 className="font-semibold text-foreground text-sm sm:text-base">Innovation</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground text-center">Cutting-edge therapeutics</p>
                    </div> */}
                    <div className="bg-card rounded-xl p-3 sm:p-6 shadow-lg card-hover border border-border">
                      <div className="flex flex-col items-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 text-center">
                        <Users className="h-6 sm:h-8 w-6 sm:w-8 text-primary flex-shrink-0" />
                        <h4 className="font-semibold text-foreground text-sm sm:text-base">Impact</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground text-center">Global patient reach</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 bg-muted/30">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center space-y-3">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-2">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-foreground font-['Space_Grotesk']">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* About Section */}
      <section className="py-10 lg:py-10">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-secondary/20">
              About Apken Pharma
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground font-['Space_Grotesk']">
              Built on Scientific Excellence
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Apken Pharma is built on scientific excellence, robust quality systems, and patient-centric values. We develop, manufacture, and commercialize pharmaceutical products that meet stringent global regulatory standards.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="card-hover border-border">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Biosimilars Division Highlight */}
      <section className="py-10 lg:py-10 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-accent/10 text-accent hover:bg-accent/20 border-accent/20">
                Apken Biosimilars
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground font-['Space_Grotesk']">
                Leading Biosimilar Innovation
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our biosimilar division focuses on developing safe, effective, and affordable alternatives to reference biologics across key therapeutic areas.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Advanced Development</h4>
                    <p className="text-muted-foreground">Cell line development and analytical characterization</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Clinical Excellence</h4>
                    <p className="text-muted-foreground">Comprehensive clinical and regulatory support</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Patient Safety</h4>
                    <p className="text-muted-foreground">Robust pharmacovigilance systems</p>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="grid grid-cols-2 gap-4">
              {therapeuticAreas.map((area, index) => (
                <Card key={index} className="card-hover">
                  <CardHeader>
                    <div className={`w-3 h-3 rounded-full ${area.color} mb-2`} />
                    <CardTitle className="text-lg">{area.name}</CardTitle>
                    <CardDescription>{area.count}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div> */}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 lg:py-10">
        <div className="container-custom">
          <Card className="bg-gradient-to-br from-primary to-primary-dark text-white border-0 shadow-xl">
            <CardContent className="p-12 lg:p-16">
              <div className="max-w-3xl mx-auto text-center space-y-6">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Space_Grotesk']">
                  Join Us in Advancing Healthcare
                </h2>
                <p className="text-lg text-white/90 leading-relaxed">
                  Explore career opportunities at Apken Pharma and be part of a team dedicated to improving patient outcomes through innovation and excellence.
                </p>
                <div className="flex justify-center">
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 border-white/20 text-white hover:bg-white/20">
                      Get in Touch
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
